// imports
import Model from '$dependencies/Model';
import PhotoCollection from '$collections/PhotoCollection';
import Config from '../../Config';
import TaxonomyModel from '$models/TaxonomyModel';
import BinModel from '$models/bin/BinModel';

const FIELDS = {
	uuid: { type: String, default: '', identifier: true },
	customId: { type: String, default: '' },
	created: { type: String, default: '' },
	doneDate: { type: String, default: '' },
	title: { type: String, default: '' },
	archived: { type: Boolean, default: false },
	locked: { type: Boolean, default: false },
	city: { type: String, default: '' },
	description: { type: String, default: '' },
	legalInfo: { type: String, default: '' },
	declineRemark: { type: String, default: '' },
	priority: { type: Boolean, default: false },
	reporterEmail: { type: String, default: '' },
	reporterName: { type: String, default: '' },
	reporterPhone: { type: String, default: '' },
	reporterSubscribed: { type: Boolean, default: false },
	street: { type: String, default: '' },
	volume: { type: Number, default: 0 },
	volumeReal: { type: Number, default: 0 },
	candidateStatus: { type: Number, default: 1 },
	location: { type: Object, default: () => ({ lat: null, lon: null }) },
	declineReason: { type: TaxonomyModel, default: () => new TaxonomyModel() },
	postalCode: { type: TaxonomyModel, default: () => new TaxonomyModel() },
	category: { type: TaxonomyModel, default: () => new TaxonomyModel() },
	reportType: { type: TaxonomyModel, default: () => new TaxonomyModel() },
	status: { type: TaxonomyModel, default: () => new TaxonomyModel() },
	team: { type: TaxonomyModel, default: () => new TaxonomyModel() },
	images: { type: Object, default: () => new PhotoCollection(), referenceType: 'file--file' },
	remarks: { type: Array, default: () => [] },
	bin: { type: Object, default: () => emptyBin() },
	access: { type: Object, default: () => ({}) },
	user: { type: Object, default: () => ({}) },
	apiPartners: { type: Object, default: () => null },
	externalId: { type: String, default: '' },
	externalUrl: { type: String, default: '' },
	deletePermission: { type: Boolean, default: false },
	hasEditPermission: { type: Boolean, default: false },
	noEditReason: { type: String, default: '' },
};

// class definition
export default class IncidentModel extends Model {
	constructor(args = {}) {
		args = Object.assign({}, args);
		args.fields = FIELDS;
		args.identifier = args.identifier || 'uuid';
		super(args);
	}

	fetch(data) {
		this.setConfig('url', `${Config.getUrl('c_incident')}/${this.uuid || this.id}`);
		return super.fetch(data).then((result) => this.hydrateBinType().then(() => result));
	}

	hydrateBinType() {
		const bin = this.bin || {};
		const id = bin.uuid || bin.id;
		const hasType = (bin.fraction && bin.fraction.uuid) || (bin.type && bin.type.uuid) || (bin.bin_type && bin.bin_type.id);
		if (!id || hasType) return Promise.resolve(this);

		const binModel = new BinModel();
		binModel.uuid = id;
		if (this.config && this.config.headers) {
			binModel.setConfig('headers', this.config.headers);
		}

		return binModel
			.fetch()
			.then(() => {
				this.bin = normalizeBin({
					...bin,
					type: binModel.type,
					fraction: binModel.fraction,
					customBinId: binModel.customBinId || bin.customBinId,
					binId: binModel.binId || bin.binId,
				});
				return this;
			})
			.catch(() => this);
	}

	post(data) {
		this.setConfig('url', Config.getUrl('c_incident'));
		return super.post(prepareDataForPost(data || this.toObject(true)));
	}

	parse(data) {
		if (!data) return {};

		const payload = data.data ? { ...data.data } : { ...data };
		if (payload.id && !payload.uuid) {
			payload.uuid = payload.id;
		}

		normalizeIncidentPayload(payload);
		hydrateImages(this, payload);
		applyAccess(payload);

		return super.parse(payload);
	}

	patch(data, options = {}) {
		this.setConfig('url', `${Config.getUrl('c_incident')}/${this.uuid || this.id}`);
		const payload = prepareDataForPatch(data || this.toObject(true));
		return super.patch(cleanUpPatchFields(payload, options.fieldsToPatch));
	}

	patchRemarks(remarks) {
		this.setConfig('url', `${Config.getUrl('c_incident')}/${this.uuid || this.id}`);
		this.unsetConfig('data');
		// Pro API expects remarks as string[] (not [{ content }])
		const payload = remarkContents(remarks);
		return super.patch({ remarks: payload }).then((result) => {
			const returned = result && result.data && result.data.remarks;
			if (!Array.isArray(returned)) {
				this.set({ remarks: normalizeRemarks(payload) });
			}
			return result;
		});
	}

	delete() {
		this.setConfig('url', `${Config.getUrl('c_incident')}/${this.uuid || this.id}`);
		return super.delete();
	}

	checkforChangedFields(other) {
		const current = prepareDataForPatch(this.toObject(true));
		const next = prepareDataForPatch((other && other.toObject ? other.toObject(true) : other) || {});
		return Object.keys(next).filter((key) => JSON.stringify(current[key]) !== JSON.stringify(next[key]));
	}

	resolvePostalCode(postalCodes) {
		if (!postalCodes) return this;

		const code = this.postalCode && (this.postalCode.postalCode || this.postalCode.title);
		const uuid = this.postalCode && this.postalCode.uuid;
		if (!code && !uuid) return this;

		const match = postalCodes.findWhere((item) => (
			(uuid && item.uuid === uuid)
			|| (code && String(item.postalCode) === String(code))
			|| (code && String(item.title) === String(code))
		));

		if (match) {
			this.postalCode = match.clone ? match.clone(true) : match;
		}

		return this;
	}

	get id() {
		return this.uuid;
	}

	set id(val) {
		this.uuid = val;
	}

	get custom_id() {
		return this.customId;
	}

	set custom_id(val) {
		this.customId = val;
	}

	get legal_info() {
		return this.legalInfo;
	}

	set legal_info(val) {
		this.legalInfo = val;
	}

	get real_volume() {
		return this.volumeReal;
	}

	set real_volume(val) {
		this.volumeReal = val;
	}

	get reporter_email() {
		return this.reporterEmail;
	}

	set reporter_email(val) {
		this.reporterEmail = val;
	}

	get reporter_name() {
		return this.reporterName;
	}

	set reporter_name(val) {
		this.reporterName = val;
	}

	get reporter_phone() {
		return this.reporterPhone;
	}

	set reporter_phone(val) {
		this.reporterPhone = val;
	}

	get reporter_subscribed() {
		return this.reporterSubscribed;
	}

	set reporter_subscribed(val) {
		this.reporterSubscribed = val;
	}

	get declined_reason() {
		return this.declineReason;
	}

	set declined_reason(val) {
		this.declineReason = val;
	}

	get declined_remark() {
		return this.declineRemark;
	}

	set declined_remark(val) {
		this.declineRemark = val;
	}

	get report_type() {
		return this.reportType;
	}

	set report_type(val) {
		this.reportType = val;
	}

	get post_code() {
		return this.postalCode && (this.postalCode.postalCode || this.postalCode.title || '');
	}

	get post_code_ref() {
		return this.postalCode;
	}

	get external_id() {
		return this.externalId;
	}

	get external_url() {
		return this.externalUrl;
	}

	get fullAddress() {
		let full = '';
		const postal = this.post_code;

		if (this.street) full += this.street;
		if (this.street && (postal || this.city)) full += ', ';
		if (postal) full += postal;
		if (this.city) full += ` ${this.city}`;

		return full;
	}

	get coordinates() {
		return { latitude: this.location.lat, longitude: this.location.lon };
	}

	get gmapCoordinates() {
		return { lat: this.location.lat, lng: this.location.lon };
	}

	get latitude() {
		return this.location.lat;
	}

	get longitude() {
		return this.location.lon;
	}

	set latitude(val) {
		this.location.lat = val;
	}

	set longitude(val) {
		this.location.lon = val;
	}
}

function normalizeIncidentPayload(payload) {
	// Map snake_case API fields onto model field names before Model.set.
	if (payload.reporter_subscribed !== undefined && payload.reporterSubscribed === undefined) {
		payload.reporterSubscribed = !!payload.reporter_subscribed;
	}
	if (payload.reporter_email !== undefined && payload.reporterEmail === undefined) {
		payload.reporterEmail = payload.reporter_email;
	}
	if (payload.reporter_name !== undefined && payload.reporterName === undefined) {
		payload.reporterName = payload.reporter_name;
	}
	if (payload.reporter_phone !== undefined && payload.reporterPhone === undefined) {
		payload.reporterPhone = payload.reporter_phone;
	}
	if (payload.declined_reason !== undefined && payload.declineReason === undefined) {
		payload.declineReason = payload.declined_reason;
	}
	if (payload.declined_remark !== undefined && payload.declineRemark === undefined) {
		payload.declineRemark = payload.declined_remark;
	}
	if (payload.report_type !== undefined && payload.reportType === undefined) {
		payload.reportType = payload.report_type;
	}
	if (payload.volume_real !== undefined && payload.volumeReal === undefined) {
		payload.volumeReal = payload.volume_real;
	}
	if (payload.custom_id !== undefined && payload.customId === undefined) {
		payload.customId = payload.custom_id;
	}
	if (payload.legal_info !== undefined && payload.legalInfo === undefined) {
		payload.legalInfo = payload.legal_info;
	}
	if (payload.done_date !== undefined && payload.doneDate === undefined) {
		payload.doneDate = payload.done_date;
	}

	['status', 'team', 'category', 'reportType', 'declineReason'].forEach((key) => {
		if (payload[key] !== undefined) {
			payload[key] = normalizeTerm(payload[key]);
		}
	});

	payload.postalCode = normalizePostalCode(payload.postalCode);
	payload.bin = normalizeBin(payload.bin);
	payload.user = normalizeUser(payload.user);
	if (payload.remarks !== undefined) {
		payload.remarks = normalizeRemarks(payload.remarks);
	}
	payload.access = payload.access || {};
	payload.location = {
		lat: payload.location && payload.location.lat != null ? payload.location.lat : null,
		lon: payload.location && payload.location.lon != null ? payload.location.lon : null,
	};

	if (payload.volume != null) payload.volume = Number(payload.volume) || 0;
	if (payload.volumeReal != null) payload.volumeReal = Number(payload.volumeReal) || 0;
}

function normalizeTerm(value) {
	if (!value) return {};
	if (typeof value === 'string') {
		return { uuid: value, id: value, value };
	}

	const uuid = value.uuid || value.id || value.value || '';
	const title = value.title || value.name || value.label || '';

	return {
		...value,
		uuid,
		id: uuid,
		value: uuid,
		title,
		name: value.name || title,
		label: value.label || title,
		machineName: value.machineName || value.machine_name || '',
		color: value.color || '',
	};
}

function normalizePostalCode(value) {
	if (value === undefined || value === null || value === '') return {};
	if (typeof value === 'string' || typeof value === 'number') {
		const code = String(value);
		return {
			postalCode: Number(value) || value,
			title: code,
			name: code,
			label: code,
		};
	}

	const uuid = value.uuid || value.id || '';
	const code = value.postalCode || value.title || value.name || '';
	return {
		...value,
		uuid,
		id: uuid,
		value: uuid,
		postalCode: value.postalCode || Number(code) || code,
		title: value.title || String(code),
		name: value.name || value.title || String(code),
		label: value.label || value.title || String(code),
	};
}

function emptyBin() {
	return {
		id: '',
		uuid: '',
		custom_id: '',
		customId: '',
		customBinId: '',
		binId: '',
		bin_type: { id: '', uuid: '' },
		type: {},
		fraction: {},
	};
}

function normalizeBin(value) {
	if (!value) return emptyBin();

	const uuid = value.uuid || value.id || '';
	const type = value.type || value.binType || value.bin_type || {};
	const fraction = value.fraction || {};
	const iconTerm = (fraction && (fraction.uuid || fraction.id)) ? fraction : type;
	const typeId = iconTerm.uuid || iconTerm.id || '';
	const customId = value.customBinId || value.custom_id || value.customId || value.binId || '';

	return {
		id: uuid,
		uuid,
		custom_id: customId,
		customId,
		customBinId: customId,
		binId: value.binId || customId,
		bin_type: {
			...iconTerm,
			id: typeId,
			uuid: typeId,
		},
		type,
		fraction,
	};
}

function normalizeUser(value) {
	if (!value) return {};
	const fullName = value.fullName || value.full_name || '';
	return {
		...value,
		fullName,
		full_name: fullName,
	};
}

function normalizeRemarks(remarks) {
	if (!Array.isArray(remarks)) return [];
	return remarks.map((value, index) => normalizeRemark(value, index)).filter(Boolean);
}

function remarkContents(remarks) {
	if (!Array.isArray(remarks)) return [];
	return remarks.map((remark) => {
		if (typeof remark === 'string') return remark.trim();
		if (!remark) return '';
		let content = remark.content;
		if (content && typeof content === 'object') {
			content = content.value || content.processed || '';
		}
		return content == null ? '' : String(content).trim();
	}).filter((content) => content.length);
}

function normalizeRemark(value, index = 0) {
	if (typeof value === 'string') {
		const content = value.trim();
		if (!content) return null;
		return {
			id: `index-${index}`,
			uuid: `index-${index}`,
			content,
			created: null,
			public: false,
			user: normalizeRemarkUser(null),
		};
	}

	if (!value || typeof value !== 'object') return null;

	const id = value.uuid || value.id || `index-${index}`;
	let content = value.content;
	if (content && typeof content === 'object') {
		content = content.value || content.processed || '';
	}

	return {
		id,
		uuid: id,
		content: content == null ? '' : String(content).trim(),
		created: normalizeCreated(value.created),
		public: value.public === true || value.public === 1 || value.public === '1',
		user: normalizeRemarkUser(value.user),
	};
}

function normalizeRemarkUser(value) {
	if (!value) {
		return { id: '', full_name: '', fullName: '', first_name: '', last_name: '' };
	}
	if (typeof value === 'string') {
		return { id: '', full_name: value, fullName: value, first_name: value, last_name: '' };
	}

	const user = normalizeUser(value);
	const first = value.first_name || value.firstName || '';
	const last = value.last_name || value.lastName || '';
	const full = user.full_name || `${first} ${last}`.trim();

	return {
		...user,
		id: user.uuid || user.id || '',
		full_name: full,
		fullName: full,
		first_name: first || full,
		last_name: last,
	};
}

function normalizeCreated(value) {
	if (value instanceof Date) return value;
	if (value == null || value === '') return null;

	const numeric = Number(value);
	if (String(value).trim() !== '' && !Number.isNaN(numeric) && String(value).length <= 13) {
		return new Date(numeric < 1e12 ? numeric * 1000 : numeric);
	}

	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? value : parsed;
}

function hydrateImages(model, payload) {
	if (!Array.isArray(payload.images) || !model.images) return;

	const images = payload.images;
	delete payload.images;

	model.images.clear();
	images.forEach((image) => {
		const id = image.uuid || image.id || '';
		model.images.set({
			id,
			url: image.url || '',
			original: image.original || image.originalUrl || image.url || '',
			originalUrl: image.originalUrl || image.original_url || image.original || image.url || '',
		});
	});
}

function applyAccess(payload) {
	const access = payload.access || {};
	payload.deletePermission = access.delete === true;
	payload.hasEditPermission = access.update === true;
	payload.noEditReason = payload.noEditReason || '';
}

function termId(term) {
	if (!term) return '';
	if (typeof term === 'string' || typeof term === 'number') return String(term);
	return term.uuid || term.id || '';
}

function imageIds(images) {
	const list = Array.isArray(images) ? images : [];
	return list.map((image) => image && (image.id || image.uuid)).filter(Boolean);
}

function prepareDataForPatch(data) {
	const payload = { ...data };

	['status', 'team', 'declineReason', 'category', 'reportType'].forEach((key) => {
		if (payload[key] !== undefined) {
			payload[key] = termId(payload[key]) || undefined;
		}
	});

	if (payload.postalCode !== undefined) {
		payload.postalCode = termId(payload.postalCode) || undefined;
	}

	if (payload.location && payload.location.lat != null && payload.location.lon != null) {
		payload.location = `POINT (${payload.location.lon} ${payload.location.lat})`;
	}

	if (payload.images !== undefined) {
		payload.images = imageIds(payload.images);
	}

	if (payload.bin) {
		payload.bin = payload.bin.id || payload.bin.uuid || undefined;
		if (!payload.bin) delete payload.bin;
	}

	stripReadOnlyFields(payload);
	return payload;
}

function prepareDataForPost(data) {
	const payload = prepareDataForPatch(data);

	if (payload.images) delete payload.images;
	delete payload.declineReason;

	const remarks = remarkContents(data && data.remarks);
	if (remarks.length) payload.remarks = remarks;

	return payload;
}

function stripReadOnlyFields(payload) {
	[
		'access',
		'deletePermission',
		'hasEditPermission',
		'noEditReason',
		'user',
		'uuid',
		'created',
		'doneDate',
		'customId',
		'apiPartners',
		'externalId',
		'externalUrl',
		'locked',
		'remarks',
	].forEach((key) => {
		delete payload[key];
	});
}

function cleanUpPatchFields(data, fieldsToPatch) {
	if (!fieldsToPatch) return data;
	if (data && fieldsToPatch) {
		for (const field in data) {
			if (!fieldsToPatch.includes(field)) {
				delete data[field];
			}
		}
	}
	return data;
}
